import { Injectable, signal } from '@angular/core';

export type DataRecord = Record<string, unknown>;

export interface LockedSpace extends DataRecord {
  lockId?: string;
  systemProvider?: string;
}

export interface AccessRequest extends DataRecord {
  accessId?: string;
  deliveryItem?: DataRecord;
}

export interface AccessKey extends DataRecord {
  lockedSpaceId?: string;
  itemId?: string;
}

export const DEFAULT_LOCKED_SPACE: LockedSpace = {
  keyUserId: 'PNDelPrsnl1234',
  shipperName: 'Postnord',
  requesterId: 'ID1234',
  email: 'sesamdummy@example.com',
  phoneNumber: '',
  streetAddress: 'Storgatan',
  streetNumber: '88b',
  postalCode: '11622',
  city: 'Storstad',
  country: 'SE',
  latitude: 57.1,
  longitude: 11.1,
  length: 10,
  width: 5,
  height: 7,
  systemProviders: ['assa'],
};

@Injectable({ providedIn: 'root' })
export class WorkflowState {
  readonly lockedSpaces = signal<ReadonlyMap<string, LockedSpace>>(new Map());
  readonly archivedLockedSpaces = signal<ReadonlyMap<string, LockedSpace>>(new Map());
  readonly accessRequests = signal<ReadonlyMap<string, AccessRequest>>(new Map());
  readonly pendingAccessRequests = signal<ReadonlyMap<string, DataRecord>>(new Map());
  readonly accessKeys = signal<ReadonlyMap<string, AccessKey>>(new Map());

  replaceLockedSpaces(items: readonly LockedSpace[], metadata: LockedSpace = {}): void {
    const next = new Map<string, LockedSpace>();
    for (const item of items) {
      const key = `${String(item['lockId'] ?? '')}${String(item['systemProvider'] ?? '')}${crypto.randomUUID()}`;
      next.set(key, { ...DEFAULT_LOCKED_SPACE, ...metadata, ...item });
    }
    this.lockedSpaces.set(next);
    this.archive(next);
  }

  updateLockedSpace(id: string, value: LockedSpace): void {
    const next = new Map(this.lockedSpaces());
    next.set(id, { ...next.get(id), ...value });
    this.lockedSpaces.set(next);
    this.archive(next);
  }

  addAccessRequest(value: AccessRequest): void {
    if (!value.accessId) {
      return;
    }
    const next = new Map(this.accessRequests());
    next.set(value.accessId, value);
    this.accessRequests.set(next);
  }

  setPendingAccessRequest(id: string, value: DataRecord): void {
    const next = new Map(this.pendingAccessRequests());
    next.set(id, { ...next.get(id), ...value });
    this.pendingAccessRequests.set(next);
  }

  removePendingAccessRequest(id: string): void {
    const next = new Map(this.pendingAccessRequests());
    next.delete(id);
    this.pendingAccessRequests.set(next);
  }

  clearPendingAccessRequests(): void {
    this.pendingAccessRequests.set(new Map());
  }

  replaceAccessKeys(items: ReadonlyMap<string, AccessKey>): void {
    this.accessKeys.set(new Map(items));
  }

  addAccessKey(value: AccessKey): void {
    if (!value.lockedSpaceId) {
      return;
    }
    const next = new Map(this.accessKeys());
    next.set(value.lockedSpaceId, value);
    this.accessKeys.set(next);
  }

  private archive(items: ReadonlyMap<string, LockedSpace>): void {
    this.archivedLockedSpaces.set(new Map([...this.archivedLockedSpaces(), ...items]));
  }
}